import './AreaIndicatorEditForm.css';

import React, { useContext, useEffect, useState } from 'react';
import Frame from '../../../b2b-common/Frame';
import { IndicatorDetailsFrame } from './AreaIndicatorCard';
import EditText from '../../../b2b-common/EditText';
import WeightRating from './WeightRating';
import FieldComponent from '../../../b2b-common/FieldComponent';
import CronExpressionUpdater from '../CronExpressionUpdater';
import BooleanIconToggle from '../../../b2b-common/BooleanIconToggle';
import { handleObjectFieldChange, truncateText } from '../../../b2b-common/helpers';
import { Edit, EditOff, Sensors, SensorsOff } from '@mui/icons-material';
import { SelectIndicatorContext } from '../../pages/AreaIndicatorEditPage';
import { getSensorModelImage } from '../../../b2b-sampling/components/SensorCard';
import RichDropdown from '../../../b2b-common/RichDropdown';
import { renderToString } from 'react-dom/server';
import DatesRangeEditor from '../dates/DatesRangeEditor';
import { getDisabledDateRanges } from '../helper';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

const WeightRatingField = ({ areaIndicator, onWeightChange }) => {

    const { t } = useB2BTranslation('b2b-plans');

    return (
        <FieldComponent
            label={t('Weight / Relevance')}
            component={<WeightRating initialWeight={areaIndicator.areaIndicatorWeight} onWeightChange={onWeightChange} />}
            className={'area-indicator-edit-form-weight'}
        />
    );
};

function getOrganizationSensorModelImage(iotFrameworks, frameworkId, levelN_ItemId) {
    if (iotFrameworks) {
        const framework = iotFrameworks.find(framework => framework.frameworkId === frameworkId);
        return getSensorModelImage(framework, levelN_ItemId);
    }
    return null;
}

const AreaIndicatorEditForm = ({ areaIndicator, setAreaIndicator }) => {

    const { t } = useB2BTranslation('b2b-plans');
    const { sensorConfigs, samplingConfiguration, setSamplingConfiguration, frameworkIndicators } = useContext(SelectIndicatorContext);

    const [selectedSensor, setSelectedSensor] = useState('');

    const voidSamplingConfiguration = {
        areaIndicatorId: null,
        sourceConfigId: null,
        indicatorUuid: areaIndicator?.indicatorDTO?.indicatorUUID ?? '',
        readFrequencyInMinutes: 60,
        lastReadDate: null,
        startDate: areaIndicator?.area?.areaStartDate ?? new Date().toISOString(),
        endDate: areaIndicator?.area?.areaEndDate ?? new Date().toISOString(),
        enabled: true,
    };

    useEffect(() => {
        if (!samplingConfiguration) {
            setSamplingConfiguration(voidSamplingConfiguration);
        } else {
            setSelectedSensor(samplingConfiguration.sourceConfigId);
        }
    }, [samplingConfiguration, setSamplingConfiguration]);

    const isAutomaticSampling = areaIndicator.indicatorDTO.automaticSampling;

    const iotFrameworks = frameworkIndicators ?
        frameworkIndicators.all.filter(framework => framework.frameworkMicroserviceAcronym === 'IOTFW')
        : [];
    // console.log(iotFrameworks);
    const sensorsItems = isAutomaticSampling ?
        (sensorConfigs && sensorConfigs.length > 0) ?
            sensorConfigs
                .filter(config => config.supportedIndicatorUUIDs.includes(areaIndicator.indicatorDTO.indicatorUUID))
                .map((config, index) => ({
                    value: config.sourceConfigId,
                    imageSrc: getOrganizationSensorModelImage(iotFrameworks, config.frameworkId, config.levelN_ItemId),
                    content: renderToString(
                        <div className='sensor-list-item'>
                            <div className='sensor-item-name'>{config.sourceName}</div>
                            <div className='sensor-item-description'>{truncateText(config.sourceDescription, 10)}</div>
                        </div>
                    )
                }))
            : []
        : [];
    // console.log(sensorsItems);

    const handleSelectSensor = (event) => {
        setSelectedSensor(event.target.value);
        setSamplingConfiguration(prevState => ({ ...prevState, sourceConfigId: event.target.value }));
    };

    return (
        <div className='area-indicator-edit-form-container'>
            <div className='area-indicator-edit-form-common'>
                <Frame title={t('Area indicator fields')} className='area-indicator-edit-form-frame-details transparent-no-title-theme'>
                    <div className='area-indicator-edit-form-name-and-edit'>
                        <EditText object={areaIndicator} objectField={'areaIndicatorName'} setObjectField={setAreaIndicator} label={t('Name')} />
                        <BooleanIconToggle
                            value={areaIndicator.readOnly}
                            onChange={(value) => handleObjectFieldChange('readOnly', value, setAreaIndicator)}
                            trueIcon={<EditOff />}
                            trueText={t('Read only')}
                            falseIcon={<Edit />}
                            falseText={t('Edit mode')}
                            className='area-indicator-edit-form-read-only-toggle'
                        />
                    </div>
                    <div className='area-indicator-edit-form-cron-and-weight'>
                        {!isAutomaticSampling && (
                            <CronExpressionUpdater initialCronExpression={areaIndicator.samplingPeriodCronExpression} onUpdate={(cronExpression) => setAreaIndicator(prevState => ({ ...prevState, samplingPeriodCronExpression: cronExpression }))} />
                        )}
                        <WeightRatingField areaIndicator={areaIndicator} onWeightChange={(weight) => setAreaIndicator(prevState => ({ ...prevState, areaIndicatorWeight: weight }))} />
                    </div>
                </Frame>
            </div>
            {isAutomaticSampling && samplingConfiguration && (
                <Frame title={t('Automatic sampling configuration')} className='area-indicator-edit-form-automatic-sampling-frame'>
                    <div className='area-indicator-edit-form-drop-enabled'>
                        <RichDropdown
                            inputLabel={t('Select sensor')}
                            items={sensorsItems}
                            selectedItem={selectedSensor}
                            handleChange={handleSelectSensor}
                            className='area-indicator-edit-form-sensor-dropdown'
                        />
                        <BooleanIconToggle
                            value={samplingConfiguration.enabled}
                            onChange={(value) => setSamplingConfiguration(prevState => ({ ...prevState, enabled: value }))}
                            trueIcon={<Sensors />}
                            trueText={t('Automatic sampling is enabled')}
                            falseIcon={<SensorsOff />}
                            falseText={t('Automatic sampling is disabled')}
                            className='area-indicator-edit-form-sampling-enabled-toggle'
                        />
                    </div>
                    <div className='area-indicator-edit-form-frequency-and-dates'>
                        <EditText object={samplingConfiguration} objectField={'readFrequencyInMinutes'} setObjectField={setSamplingConfiguration} label={t('Sampling frequency')} className={'area-indicator-edit-form-sampling-frequency'} suffix={t('Minutes')} />
                        <DatesRangeEditor
                            datesRangeHolder={samplingConfiguration}
                            setDatesRangeHolder={setSamplingConfiguration}
                            startDateLabel='Start date'
                            endDateLabel='End date'
                            startDateField='startDate'
                            endDateField='endDate'
                            className={'area-indicator-edit-form-sampling-dates-range'}
                            disabledDateRanges={getDisabledDateRanges(areaIndicator)}
                        />
                    </div>
                </Frame>
            )}
        </div>
    );
};

export default AreaIndicatorEditForm;